import { useState, useEffect, useRef, useCallback } from "react";

const CACHE_TTL_MS = 12 * 60 * 60 * 1000; // 12 hours

interface CacheEntry<T> {
  data: T;
  savedAt: number;
}

export interface CacheControls {
  hasCachedData: boolean;
  loadCache: () => void;
  skipCache: () => void;
}

/**
 * A drop-in replacement for useState that persists to localStorage with a 12-hour TTL.
 * On reload, the cached value is restored if it is less than 12 hours old.
 * Calling clearCache() removes the entry so the next reload starts fresh.
 *
 * @param cacheKey  - unique localStorage key
 * @param defaultValue - initial value used when no valid cache exists
 * @param excludeKeys  - keys to omit from the persisted snapshot (e.g. computed fields)
 */
function useFormCache<T extends Record<string, unknown>>(
  cacheKey: string,
  defaultValue: T,
  excludeKeys: (keyof T)[] = []
): [T, React.Dispatch<React.SetStateAction<T>>, () => void, CacheControls] {
  const defaultRef = useRef(defaultValue);
  const excludeRef = useRef(excludeKeys);

  // Check if cached data exists and is valid BEFORE setting state
  const checkCachedData = (): boolean => {
    try {
      const raw = localStorage.getItem(cacheKey);
      if (raw) {
        const entry: CacheEntry<Partial<T>> = JSON.parse(raw);
        const age = Date.now() - entry.savedAt;
        if (age < CACHE_TTL_MS) {
          return true;
        }
        localStorage.removeItem(cacheKey);
      }
    } catch {
      // Silently ignore parse / storage errors
    }
    return false;
  };

  const [hasCachedData] = useState<boolean>(checkCachedData());
  // When a valid cache exists, wait for explicit load/skip before writing new snapshots.
  const persistEnabledRef = useRef<boolean>(!hasCachedData);
  const [value, setValueInternal] = useState<T>(defaultRef.current); // Start with default
  const [useCacheKey] = useState<string>(cacheKey); // Track the cache key to prevent stale closure

  // Wrapped setter that also persists to localStorage
  const setValue: React.Dispatch<React.SetStateAction<T>> = useCallback(
    (updater) => {
      setValueInternal((prev) => {
        const next =
          typeof updater === "function"
            ? (updater as (prev: T) => T)(prev)
            : updater;
        if (!persistEnabledRef.current) {
          return next;
        }
        try {
          // Strip excluded keys before persisting
          const snapshot = { ...next } as Partial<T>;
          excludeRef.current.forEach((k) => delete snapshot[k]);
          const entry: CacheEntry<Partial<T>> = {
            data: snapshot,
            savedAt: Date.now(),
          };
          localStorage.setItem(useCacheKey, JSON.stringify(entry));
        } catch {
          // Silently ignore storage errors (e.g. private browsing quota)
        }
        return next;
      });
    },
    [useCacheKey]
  );

  // Clears the localStorage entry and resets state to defaults
  const clearCache = useCallback(() => {
    persistEnabledRef.current = true;
    localStorage.removeItem(useCacheKey);
    setValueInternal(defaultRef.current);
  }, [useCacheKey]);

  // Load the cached data into the form
  const loadCache = useCallback(() => {
    try {
      const raw = localStorage.getItem(useCacheKey);
      if (raw) {
        const entry: CacheEntry<Partial<T>> = JSON.parse(raw);
        const age = Date.now() - entry.savedAt;
        if (age < CACHE_TTL_MS) {
          // Merge with defaultValue so any new fields added later are present
          persistEnabledRef.current = true;
          setValueInternal({ ...defaultRef.current, ...entry.data } as T);
          return;
        }
        localStorage.removeItem(useCacheKey);
      }
    } catch {
      // Silently ignore parse / storage errors
    }
    persistEnabledRef.current = true;
    setValueInternal(defaultRef.current);
  }, [useCacheKey]);

  // Skip the cache and keep the default
  const skipCache = useCallback(() => {
    persistEnabledRef.current = true;
    localStorage.removeItem(useCacheKey);
    setValueInternal(defaultRef.current);
  }, [useCacheKey]);

  // Keep excludeRef in sync without triggering re-renders
  useEffect(() => {
    excludeRef.current = excludeKeys;
  });

  const cacheControls: CacheControls = { hasCachedData, loadCache, skipCache };
  return [value, setValue, clearCache, cacheControls];
}

export default useFormCache;



